/**
 * Match-each-statement questions (algebraic expressions like 5k, 500-5m).
 */

export const MATCH_EACH_STATEMENT_STEM = /\bmatch\s+each\s+statement\b/i;

export function isAlgebraExpressionToken(raw) {
  const s = String(raw ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return false;
  if (/^[-+]?\d*\.?\d+([eE][-+]?\d+)?%?$/.test(s)) return true;
  if (/^\d+\/\d+$/.test(s)) return true;
  if (/^_{2,}|\.{3,}|…+$/.test(s)) return false;
  if (/^[\d.]+\s*[-+×÷]\s*[\d.a-z()+£\s-]+$/i.test(s)) return true;
  if (/^\d+\([a-z0-9+\-×÷\s]+\)$/i.test(s)) return true;
  if (/^\d+[a-z](?:\s*[+\-×÷]\s*[\d.a-z()+]+)*$/i.test(s)) return true;
  if (/^[a-z](?:\s*[+\-×÷]\s*(?:\d+[a-z]?|[a-z]))+/i.test(s)) return true;
  if (/^\d+[a-z]$/i.test(s)) return true;
  return false;
}

export function parseMatchingExpressionTableFromText(text) {
  const rows = [];
  for (const line of String(text).split("\n")) {
    if (!/^\s*\|/.test(line)) continue;
    if (/^\s*\|[\s\-:|]+\|/.test(line.trim())) continue;
    const cells = line
      .split("|")
      .map((c) => c.trim())
      .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
    if (cells.length !== 2) continue;
    const [statement, expr] = cells;
    if (!statement || statement.length < 5) continue;
    if (isAlgebraExpressionToken(expr)) {
      rows.push({ statement, prefilledExpression: expr });
    } else if (!expr || /^_{2,}|\.{3,}|…+$/.test(expr)) {
      rows.push({ statement, prefilledExpression: null });
    }
  }
  return rows;
}

export function isMatchEachStatementQuestion(question) {
  const stem = String(question?.question_text || question?.question || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return MATCH_EACH_STATEMENT_STEM.test(stem);
}

function uniqueBank(items) {
  const out = [];
  const seen = new Set();
  for (const raw of items) {
    const plain = String(raw ?? "")
      .replace(/<[^>]+>/g, " ")
      .trim();
    if (!plain || seen.has(plain)) continue;
    seen.add(plain);
    out.push(plain);
  }
  return out;
}

export function buildMatchingExpressionContext(question) {
  if (!isMatchEachStatementQuestion(question)) return null;
  const rawStem = String(question.question_text || question.question || "");
  const tableRows = parseMatchingExpressionTableFromText(rawStem);
  const firstDone = /\bfirst\s+one\s+is\s+done\b/i.test(rawStem.replace(/<[^>]+>/g, " "));

  const blankLabels = (question.answer_data?.blank_labels || [])
    .map((label) => String(label ?? "").replace(/<[^>]+>/g, " ").trim())
    .filter((label) => label.length > 8);

  let slotStatements = [];
  if (blankLabels.length >= 2) {
    slotStatements = blankLabels;
  } else if (tableRows.length >= 2) {
    slotStatements = (firstDone ? tableRows.slice(1) : tableRows).map((row) => row.statement);
  }

  const bank = uniqueBank([
    ...(question.options || []).map((o) => o.option_text || o.text || "").filter(isAlgebraExpressionToken),
    ...(question.answer_data?.correct_blanks || []).map(String).filter(isAlgebraExpressionToken),
    ...tableRows.map((r) => r.prefilledExpression).filter(Boolean),
  ]);

  if (bank.length < 2 || slotStatements.length < 2) return null;

  return {
    instructionHtml: rawStem
      .split("\n")
      .filter((line) => !/^\s*\|/.test(line))
      .join("\n")
      .trim(),
    displayRows: tableRows.map((row, index) => ({
      ...row,
      isExample: firstDone && index === 0,
    })),
    bank,
    slotCount: slotStatements.length,
    slotStatements,
  };
}
